import { useEffect, useRef } from 'react'
import { useRouter } from '@tanstack/react-router'

/**
 * Hook to ensure consistent loading states when navigating between pages
 * Prevents race conditions and provides smooth transitions
 */
export function useConsistentNavigation() {
  const router = useRouter()
  const navigationTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current)
      }
    }
  }, [])

  /**
   * Navigate to a route with consistent loading state
   */
  const navigateWithLoading = async (to: string, options?: { replace?: boolean }) => {
    // Clear any existing timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current)
    }

    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        router.navigate({ to, replace: options?.replace })
        resolve()
      })
    })
  }

  /**
   * Preload a route to ensure faster navigation
   */
  const preloadRoute = (to: string) => {
    router.preloadRoute({ to: to as any })
  }

  return {
    navigateWithLoading,
    preloadRoute,
  }
}
